import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Pin, PinOff, ArrowRight, BookOpen, Sparkles } from "lucide-react";
import SaveButton from "./SaveButton";
import ConceptEssayDialog from "./ConceptEssayDialog";
import { useAuth } from "../context/AuthContext";

/**
 * ConceptDetailPanel — slide-out from the right edge when a concept
 * node is tapped in Concept-mode.  Fetches /api/curriculum/concept/:term
 * and renders the world-grouped chapter list, plus inline pin and save
 * actions.
 */
export default function ConceptDetailPanel({ term, onClose }) {
  const [data, setData] = useState(null);
  const [pinned, setPinned] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [essayOpen, setEssayOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!term) return;
    let cancelled = false;
    setData(null);
    api.get(`/curriculum/concept/${encodeURIComponent(term)}?limit=12`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ worlds: [], total_chapters: 0 }); });
    // Check pin state
    if (user) {
      api.get(`/echoes/pinned/me`)
        .then(({ data }) => { if (!cancelled) setPinned((data.terms || []).includes(term)); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [term, user]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !essayOpen) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, essayOpen]);

  const togglePin = async () => {
    if (!user) { toast.info("Sign in to pin ideas."); return; }
    setPinBusy(true);
    try {
      if (pinned) {
        await api.delete(`/echoes/pin/${encodeURIComponent(term)}`);
        setPinned(false);
        toast.success(`Unpinned "${term}".`);
      } else {
        await api.post(`/echoes/pin`, { term });
        setPinned(true);
        toast.success(`Pinned "${term}".`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't toggle pin.");
    } finally {
      setPinBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex justify-end bg-ink/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid={`concept-detail-panel-${term}`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg h-full bg-cream border-l-2 border-ink shadow-[-8px_0_0_0_#1F1A17] overflow-y-auto"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-ink bg-cream hover:bg-marigold hover:text-cream text-ink flex items-center justify-center transition"
          data-testid="concept-detail-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-7 md:p-9">
          <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-marigold mb-2">Concept</div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-ink leading-tight mb-3 capitalize" data-testid="concept-detail-title">
            {term}
          </h2>

          <div className="flex items-center gap-2 flex-wrap mb-6">
            <button
              onClick={togglePin}
              disabled={pinBusy || !user}
              className={`text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition ${
                pinned
                  ? "bg-marigold text-cream border-marigold hover:bg-marigold/80"
                  : "border-ink/30 text-ink2 hover:border-marigold hover:text-marigold"
              } disabled:opacity-50`}
              data-testid="concept-detail-pin"
            >
              {pinned ? <><PinOff className="w-3.5 h-3.5" /> Pinned</> : <><Pin className="w-3.5 h-3.5" /> Pin this idea</>}
            </button>
            {data && data.world_count >= 2 && (
              <button
                onClick={() => setEssayOpen(true)}
                className="text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-marigold text-marigold bg-marigold/10 hover:bg-marigold hover:text-cream transition"
                data-testid="concept-detail-essay"
              >
                <Sparkles className="w-3.5 h-3.5" /> Read Ms. Pradhan's take
              </button>
            )}
            <Link
              to={`/search?q=${encodeURIComponent(term)}`}
              className="text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-ink/30 text-ink2 hover:border-marigold hover:text-marigold transition"
              data-testid="concept-detail-search"
            >
              Search everywhere <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {!data ? (
            <div className="py-10 text-center text-ink2" data-testid="concept-detail-loading">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" />
            </div>
          ) : data.total_chapters === 0 ? (
            <div className="py-10 text-center text-ink2 italic">Nothing carries this idea yet.</div>
          ) : (
            <>
              <p className="text-sm text-ink2 mb-5 italic">
                <span className="font-heading not-italic font-semibold text-ink">{data.total_chapters} chapters</span>
                {" "}carry this idea, across{" "}
                <span className="font-heading not-italic font-semibold text-ink">{data.world_count} worlds</span>.
              </p>
              <div className="space-y-6" data-testid="concept-detail-worlds">
                {data.worlds.map((w) => (
                  <section key={w.slug}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[9px] font-mono font-bold text-marigold uppercase tracking-[0.22em]">{w.kicker}</span>
                      <Link
                        to={`/learn/${w.slug}`}
                        className="font-heading text-base font-semibold text-ink hover:text-marigold transition"
                      >
                        {w.title}
                      </Link>
                      <span className="text-[10px] font-mono text-ink2/60 ml-auto">{w.chapter_count} chapters</span>
                    </div>
                    <ul className="rounded-2xl border-2 border-ink divide-y divide-ink/10 overflow-hidden">
                      {w.chapters.map((c) => (
                        <li
                          key={c.slug}
                          className="flex items-start px-3 py-2.5 gap-2 hover:bg-marigold/[0.06] transition"
                          data-testid={`concept-detail-chapter-${c.slug}`}
                        >
                          <Link to={`/library/${c.slug}`} className="flex-1 min-w-0 group">
                            <div className="text-[10px] font-mono text-ink/50">{c.code}</div>
                            <div className="text-sm font-heading font-semibold text-ink group-hover:text-marigold transition line-clamp-2 leading-snug">
                              {c.title}
                            </div>
                          </Link>
                          <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                            <SaveButton slug={c.slug} title={c.title} size="sm" testId={`concept-detail-save-${c.slug}`} />
                            <Link
                              to={`/library/${c.slug}`}
                              className="text-ink/40 hover:text-marigold"
                              aria-label={`Open ${c.title}`}
                            >
                              <BookOpen className="w-4 h-4" />
                            </Link>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {essayOpen && data && data.worlds && data.worlds.length >= 2 && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConceptEssayDialog
            term={term}
            worlds={data.worlds.map((w) => ({ slug: w.slug, title: w.title, kicker: w.kicker }))}
            onClose={() => setEssayOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
